/**
 * Saved views (§21) — a discoverable chip pattern. Saved filter sets render as
 * selectable chips (click to apply, active one highlighted, each with rename +
 * delete); a "+ Save current view" action names + saves the current filters; an
 * instructive empty state when none exist. Pure presentational — the container
 * (Queue) owns persistence via lib/savedViews.
 */
import { useState } from "react";
import type { SavedView } from "../../lib/savedViews.js";
import { type ViewFilter, sameViewFilter } from "../../lib/filters.js";
import { Button, Input, Icon } from "../../components/ui/index.js";

export function SavedViews({
  views,
  activeFilter,
  onApply,
  onSave,
  onRename,
  onDelete,
}: {
  views: SavedView[];
  activeFilter: ViewFilter;
  onApply: (v: SavedView) => void;
  onSave: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  const commitSave = () => {
    if (draft.trim()) onSave(draft.trim());
    setDraft("");
    setCreating(false);
  };
  const commitRename = (id: string) => {
    if (renameDraft.trim()) onRename(id, renameDraft.trim());
    setRenamingId(null);
    setRenameDraft("");
  };

  return (
    <div className="views">
      <span className="views__label">Views</span>

      {views.length === 0 && !creating && (
        <span className="views__empty">Save your current filters as a view to reuse them.</span>
      )}

      {views.map((v) => {
        if (renamingId === v.id) {
          return (
            <span key={v.id} className="view-chip view-chip--editing">
              <Input
                autoFocus
                aria-label={`Rename view ${v.name}`}
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename(v.id);
                  if (e.key === "Escape") setRenamingId(null);
                }}
                onBlur={() => commitRename(v.id)}
              />
            </span>
          );
        }
        const active = sameViewFilter(v.filter, activeFilter);
        return (
          <span key={v.id} className={"view-chip" + (active ? " view-chip--active" : "")}>
            <button type="button" className="view-chip__apply" aria-pressed={active} onClick={() => onApply(v)}>
              {active && <Icon name="check" size={12} />}
              {v.name}
            </button>
            <button
              type="button"
              className="view-chip__action"
              aria-label={`Rename view ${v.name}`}
              title="Rename"
              onClick={() => {
                setRenamingId(v.id);
                setRenameDraft(v.name);
              }}
            >
              <Icon name="edit" size={12} />
            </button>
            <button
              type="button"
              className="view-chip__action view-chip__del"
              aria-label={`Delete view ${v.name}`}
              title="Delete"
              onClick={() => onDelete(v.id)}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        );
      })}

      {creating ? (
        <span className="view-create">
          <Input
            autoFocus
            aria-label="New view name"
            placeholder="View name…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitSave();
              if (e.key === "Escape") {
                setCreating(false);
                setDraft("");
              }
            }}
          />
          <Button size="sm" onClick={commitSave} disabled={!draft.trim()}>
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setCreating(false);
              setDraft("");
            }}
          >
            Cancel
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setCreating(true)}>
          Save current view
        </Button>
      )}
    </div>
  );
}
